/**
 * Voice Mic Button Component
 *
 * Microphone button for voice input with visual states.
 * CP Admin 2077 styling with orange accents and clipPath.
 *
 * States:
 * - idle: Gray, ready to connect
 * - connecting: Yellow pulse
 * - connected: Green, ready to record
 * - listening: Green pulse, recording
 * - processing: Orange pulse
 * - speaking: Blue pulse, AI responding
 * - error: Red
 */

import React from 'react';
import { Mic, MicOff, Loader2, Volume2 } from 'lucide-react';
import { useVoice } from '../../contexts/VoiceContext';

interface VoiceMicButtonProps {
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

const SIZE_CLASSES = {
  sm: 'w-10 h-10',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
};

const ICON_SIZES = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-7 h-7',
};

export function VoiceMicButton({
  size = 'md',
  showLabel = false,
  className = '',
}: VoiceMicButtonProps) {
  const { state, isRecording, toggleRecording, error } = useVoice();

  // Determine button appearance based on state
  const getStateStyles = () => {
    switch (state) {
      case 'idle':
        return {
          bg: 'bg-slate-600 hover:bg-slate-500',
          ring: '',
          icon: <Mic className={ICON_SIZES[size]} />,
          label: 'Start Voice',
        };
      case 'connecting':
        return {
          bg: 'bg-yellow-600',
          ring: 'animate-pulse ring-2 ring-yellow-400/50',
          icon: <Loader2 className={`${ICON_SIZES[size]} animate-spin`} />,
          label: 'Connecting...',
        };
      case 'connected':
        return {
          bg: 'bg-green-600 hover:bg-green-500',
          ring: '',
          icon: <Mic className={ICON_SIZES[size]} />,
          label: 'Ready',
        };
      case 'listening':
        return {
          bg: 'bg-green-500',
          ring: 'animate-pulse ring-2 ring-green-400/75 ring-offset-1 ring-offset-slate-900',
          icon: <Mic className={ICON_SIZES[size]} />,
          label: 'Listening...',
        };
      case 'processing':
        return {
          bg: 'bg-orange-500',
          ring: 'animate-pulse ring-2 ring-orange-400/50',
          icon: <Loader2 className={`${ICON_SIZES[size]} animate-spin`} />,
          label: 'Processing...',
        };
      case 'speaking':
        return {
          bg: 'bg-blue-500',
          ring: 'animate-pulse ring-2 ring-blue-400/75',
          icon: <Volume2 className={ICON_SIZES[size]} />,
          label: 'Speaking...',
        };
      case 'error':
        return {
          bg: 'bg-red-600 hover:bg-red-500',
          ring: '',
          icon: <MicOff className={ICON_SIZES[size]} />,
          label: 'Error',
        };
      default:
        return {
          bg: 'bg-slate-600 hover:bg-slate-500',
          ring: '',
          icon: <Mic className={ICON_SIZES[size]} />,
          label: 'Voice',
        };
    }
  };

  const stateStyles = getStateStyles();
  const isDisabled = state === 'connecting' || state === 'processing' || state === 'speaking';

  const handleClick = async () => {
    try {
      await toggleRecording();
    } catch (err) {
      console.error('Voice toggle error:', err);
    }
  };

  return (
    <div className={`flex flex-col items-center gap-1 ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        disabled={isDisabled}
        className={`
          flex items-center justify-center
          ${SIZE_CLASSES[size]}
          ${stateStyles.bg}
          ${stateStyles.ring}
          text-white
          transition-all duration-200
          ${isDisabled ? 'cursor-wait' : 'cursor-pointer'}
          shadow-lg
        `}
        style={{
          clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%)',
        }}
        aria-label={stateStyles.label}
        title={error || stateStyles.label}
      >
        {stateStyles.icon}
      </button>

      {showLabel && (
        <span className="text-[10px] text-slate-400 font-mono">
          {stateStyles.label}
        </span>
      )}
    </div>
  );
}

export default VoiceMicButton;
